import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Quote, Star } from "lucide-react";
import { Hero } from "../components/Hero";
import { Marquee, SectionHead, CTABand, Counter, LogoWall } from "../components/ui-blocks";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { SERVICES, INDUSTRIES, STATS, TESTIMONIALS, LOGOS, MANIFESTO, WHY_STATS, BLOG } from "../data";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />

      {/* Trusted by */}
      <section className="py-16 sm:py-20" data-testid="logos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10"><p className="overline">Trusted by leading companies across the US</p></Reveal>
          <LogoWall logos={LOGOS} />
        </div>
      </section>

      {/* Services tetris grid */}
      <section className="py-20 sm:py-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="What We Offer" title="Staffing solutions that deliver results" subtitle="End-to-end workforce solutions tailored to your industry, scale, and timeline. We handle the talent — you focus on growth." />
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 lg:gap-6">
            {SERVICES.map((s) => (
              <StaggerItem key={s.key} className={s.span}>
                <Link to="/services" data-testid={`service-card-${s.key}`} className="group relative block h-full rounded-2xl border border-slate-800/80 bg-[#111827]/90 p-7 sm:p-8 hover-lift hover:border-amber-500/40 overflow-hidden">
                  <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-amber-500/0 group-hover:bg-amber-500/10 blur-3xl transition-all duration-500" />
                  <div className="relative flex items-start justify-between mb-6">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 group-hover:scale-110 transition-transform duration-300"><s.icon className="w-6 h-6" /></span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border border-slate-700 rounded-full px-2.5 py-1">{s.tag}</span>
                  </div>
                  <h3 className="relative text-xl sm:text-2xl font-semibold text-white mb-3 tracking-tight">{s.title}</h3>
                  <p className="relative text-slate-400 leading-relaxed text-sm sm:text-base">{s.desc}</p>
                  <span className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-amber-400 group-hover:gap-2.5 transition-all">Learn more <ArrowUpRight className="w-4 h-4" /></span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Manifesto / Why us */}
      <section className="py-20 sm:py-28 border-y border-slate-800/80 bg-gradient-to-b from-[#0B0F19] to-[#0d1220]" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="overline mb-4">Why Choose Us</p>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight">Built on trust. Driven by results.</h2>
                <p className="mt-5 text-slate-400 leading-relaxed">We don't just fill positions — we build partnerships. It's why 98% of our clients return to us year after year.</p>
                <Link to="/employers" data-testid="manifesto-cta" className="mt-7 group inline-flex items-center gap-1.5 px-5 py-3 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all">Partner With Us <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></Link>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Stagger className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {MANIFESTO.map((m) => (
                  <StaggerItem key={m.n}>
                    <div className="group h-full rounded-2xl border border-slate-800/80 bg-[#111827]/60 p-6 hover:border-amber-500/30 transition-all duration-300">
                      <div className="font-mono text-4xl font-bold text-slate-700 group-hover:text-amber-500/70 transition-colors mb-4">{m.n}</div>
                      <h3 className="text-lg font-semibold text-white mb-2 leading-snug">{m.title}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">{m.body}</p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
              <Stagger className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                {WHY_STATS.map((w) => (
                  <StaggerItem key={w.label}>
                    <div className="rounded-xl border border-slate-800/60 bg-[#0B0F19] p-5 text-center">
                      <div className="font-accent text-2xl sm:text-3xl font-bold text-amber-400">{w.value}</div>
                      <div className="text-xs text-slate-500 mt-1">{w.label}</div>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 sm:py-28" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Industries We Serve" title="Deep expertise across key sectors" subtitle="Specialized staffing solutions for the industries that power America's economy." />
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INDUSTRIES.map((ind) => (
              <StaggerItem key={ind.name}>
                <Link to="/industries" data-testid={`industry-card-${ind.name.split(" ")[0].toLowerCase()}`} className="group relative block rounded-2xl overflow-hidden border border-slate-800/80 hover-lift hover:border-amber-500/40 h-64">
                  <img src={ind.img} alt={ind.name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/70 to-[#0B0F19]/30" />
                  <div className="relative h-full flex flex-col justify-between p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/25 text-amber-400 backdrop-blur-sm"><ind.icon className="w-5 h-5" /></span>
                    <div>
                      <div className="font-mono text-xs text-amber-400 mb-1">{ind.roles} Active Roles</div>
                      <h3 className="text-xl font-semibold text-white">{ind.name}</h3>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-16 border-y border-slate-800/80 bg-[#0d1220]" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <StaggerItem key={s.label} className="text-center">
                <div className="font-accent text-4xl sm:text-5xl font-bold text-gradient-amber"><Counter value={s.value} suffix={s.suffix} /></div>
                <div className="text-sm text-slate-500 mt-2">{s.label}</div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead center overline="Testimonials" title="What our clients say" subtitle="Hear from the leaders who trust Hirezen with their most important asset — their people." />
          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t) => (
              <StaggerItem key={t.name}>
                <div className="h-full rounded-2xl border border-slate-800/80 bg-[#111827]/80 p-7 hover:border-amber-500/30 transition-all duration-300 flex flex-col">
                  <Quote className="w-8 h-8 text-amber-500/60 mb-4" />
                  <div className="flex gap-0.5 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-slate-300 leading-relaxed flex-1">{t.quote}</p>
                  <div className="mt-6 flex items-center gap-3 pt-5 border-t border-slate-800">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-bold">{t.init}</span>
                    <div><div className="font-semibold text-white text-sm">{t.name}</div><div className="text-xs text-slate-500">{t.role}</div></div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Blog preview */}
      <section className="py-20 sm:py-28 border-t border-slate-800/80" data-testid="blog-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <SectionHead overline="Insights & News" title="From the Hirezen desk" />
            <Reveal><Link to="/blog" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:gap-2.5 transition-all">All articles <ArrowUpRight className="w-4 h-4" /></Link></Reveal>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BLOG.map((b) => (
              <StaggerItem key={b.id}>
                <Link to="/blog" className="group block rounded-2xl overflow-hidden border border-slate-800/80 bg-[#111827]/60 hover-lift hover:border-amber-500/40 h-full">
                  <div className="relative h-44 overflow-hidden"><img src={b.img} alt={b.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" /><span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-[#0B0F19]/80 backdrop-blur-sm border border-slate-700 text-amber-400 rounded-full px-2.5 py-1">{b.category}</span></div>
                  <div className="p-6"><div className="font-mono text-xs text-slate-500 mb-2">{b.date} · {b.read} read</div><h3 className="text-lg font-semibold text-white leading-snug group-hover:text-amber-400 transition-colors">{b.title}</h3></div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
